<template>
  <div class="w-full">
    <template v-if="operatorNeedsValue">
      <div class="px-3 pb-2">
        <a-input
          v-model:value="searchQuery"
          placeholder="Search records..."
          allow-clear
          @click.stop=""
          @keydown.stop="$event.stopPropagation()"
          class="input_medium w-full record_type_filter_input_field"
        />
      </div>

      <div v-if="isLoading" class="flex items-center justify-center py-4">
        <a-spin size="small" />
      </div>

      <div v-else class="max-h-64 overflow-auto">
        <template v-if="filteredOptions.length > 0">
          <div v-for="group in groupedFilteredOptions" :key="group.object_id" class="mb-2">
            <div class="px-3 py-1 text-xs font-semibold secondary_color uppercase">
              {{ group.object_name }}
            </div>
            <div
              v-for="option in group.records"
              :key="option.compositeKey"
              class="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-[var(--secondary-button-bg)] transition-colors"
              :class="{ 'bg-[var(--secondary-button-bg)]': selectedValues.includes(option.compositeKey) }"
              @click.stop="toggleOption(option)"
            >
              <ThemeImage
                v-if="selectedValues.includes(option.compositeKey)"
                :icon="imageNames.TICK_GREY"
                alt="tick_icon"
                class="size-4"
              />
              <div v-else class="size-4 shrink-0" />
              <ProfileIcon :data="{ name: option.name }" :size="20" :font="10" />
              <span class="text_color text-sm truncate">{{ option.name }}</span>
            </div>
          </div>
        </template>
        <template v-else>
          <div class="flex flex-col items-center justify-center py-6">
            <span class="text-sm secondary_color">
              {{ searchQuery ? 'No records found' : 'No records available' }}
            </span>
          </div>
        </template>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import debounce from "lodash/debounce.js";
import ThemeImage from "@/src/components/ThemeImage.vue";
import ProfileIcon from "@/src/components/ProfileIcon.vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import CRMService from "@/src/api/CRMService.js";
import { requiresValue } from "@/src/constants/analyticsFilterConstants.js";

const props = defineProps({
  filterId: { type: String, required: true },
  filterName: { type: String, required: true },
  currentValue: { type: [Array, Object], default: () => [] },
  attribute: { type: Object, default: () => ({}) },
  relatedObjects: { type: Array, default: () => [] },
  showOperators: { type: Boolean, default: true },
  selectedOperator: { type: String, default: "contains_any" },
});

const emit = defineEmits(["submit", "clear"]);

const searchQuery = ref("");
const isLoading = ref(false);
const allOptions = ref([]);
const selectedValues = ref([]);

const operatorNeedsValue = computed(() => requiresValue(props.selectedOperator));

const getRelatedObjects = () => {
  return props.relatedObjects?.length
    ? props.relatedObjects
    : (props.attribute?.related_objects || []);
};

const parseCurrentValue = (value) => {
  let records = [];
  if (value?.value && !Array.isArray(value)) {
    records = Array.isArray(value.value) ? value.value : [];
  } else if (Array.isArray(value)) {
    records = value;
  }
  
  return records
    .filter(r => ( r?.object_type_id && r?.record_id))
    .map(r => `${ r.object_type_id}:${r.record_id}`);
};

const buildRecordsFromSelection = () => {
  return selectedValues.value.map(key => {
    const [objectId, entityId] = key.split(':');
    const option = allOptions.value.find(opt => opt.compositeKey === key);
    const displayName = option?.name || entityId;
    return {
      object_type_id: objectId,
      record_id: entityId,
      record_display_name: displayName,
    };
  });
};

const buildSubmitValue = (records) => {
  return props.showOperators
    ? { operator: props.selectedOperator || "contains_any", value: records }
    : records;
};

watch(
  () => props.currentValue,
  (newValue) => {
    const parsed = parseCurrentValue(newValue);
    if (JSON.stringify(selectedValues.value) !== JSON.stringify(parsed)) {
      selectedValues.value = parsed;
    }
  },
  { immediate: true, deep: true }
);

watch(
  () => props.selectedOperator,
  (newOperator, oldOperator) => {
    if (newOperator !== oldOperator) {
      if (!requiresValue(newOperator)) {
        emit("submit", {
          filterId: props.filterId,
          value: { operator: newOperator, value: null },
        });
      } else if (selectedValues.value.length > 0) {
        emit("submit", {
          filterId: props.filterId,
          value: buildSubmitValue(buildRecordsFromSelection()),
        });
      }
    }
  }
);

const debouncedSearch = debounce(performSearch, 300);

watch(searchQuery, debouncedSearch);

const filteredOptions = computed(() => {
  if (!searchQuery.value) return allOptions.value;
  const query = searchQuery.value.toLowerCase();
  return allOptions.value.filter(opt => opt.name.toLowerCase().includes(query));
});

const groupedFilteredOptions = computed(() => {
  const groups = {};
  for (const opt of filteredOptions.value) {
    if (!groups[opt.object_id]) {
      groups[opt.object_id] = {
        object_id: opt.object_id,
        object_name: opt.object_name,
        records: [],
      };
    }
    groups[opt.object_id].records.push(opt);
  }
  return Object.values(groups);
});

const toggleOption = (option) => {
  const key = option.compositeKey;
  const index = selectedValues.value.indexOf(key);
  if (index > -1) {
    selectedValues.value.splice(index, 1);
  } else {
    selectedValues.value.push(key);
  }
};

const submit = () => {
  if (!operatorNeedsValue.value) {
    return {
      filterId: props.filterId,
      value: { operator: props.selectedOperator, value: null },
    };
  }
  
  if (selectedValues.value.length === 0) return null;
  
  return {
    filterId: props.filterId,
    value: buildSubmitValue(buildRecordsFromSelection()),
  };
};

defineExpose({ submit });

async function performSearch(query = "") {
  const relatedObjects = getRelatedObjects();
  if (!relatedObjects.length) return;
  
  isLoading.value = true;
  
  try {
    const response = await CRMService.searchMultiObjects({
      object_ids: relatedObjects.map(ro => ro.object_id),
      query,
      limit: 50,
    });
    
    if (response?.success && response?.results) {
      allOptions.value = response.results.flatMap(objResult =>
        (objResult.records || []).map(record => ({
          compositeKey: `${objResult.object_id}:${record.id}`,
          object_id: objResult.object_id,
          object_name: objResult.object_name,
          entity_id: record.id,
          name: record?.attributes?.display_name || record.id,
        }))
      );
    }
  } catch (error) {
    console.error("Error loading record options:", error);
  } finally {
    isLoading.value = false;
  }
}

onMounted(() => {
  if (getRelatedObjects().length) {
    performSearch("");
  }
});

watch(
  () => props.relatedObjects,
  (newVal) => {
    if (newVal?.length && !allOptions.value.length) {
      performSearch("");
    }
  },
  { immediate: false }
);

watch(
  () => props.attribute?.related_objects,
  (newVal) => {
    if (newVal?.length && !allOptions.value.length && !props.relatedObjects?.length) {
      performSearch("");
    }
  },
  { immediate: true }
);
</script>

<style scoped>
.record_type_filter_input_field :deep(.ant-input-affix-wrapper) {
  width: 100% !important;
  max-width: 100% !important;
}
</style>
